import { io } from 'socket.io-client';
import { getToken } from '../utils/auth';

/**
 * Socket.IO Service
 * Singleton pattern for managing Socket.IO connection
 * Handles authentication, connection lifecycle, and event management
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map(); // Store event listeners for cleanup
  }

  /**
   * Initialize Socket.IO connection
   * @returns {Socket} - Socket.IO instance
   */
  connect() {
    // If socket exists and is connected, return it
    if (this.socket && this.socket.connected) {
      console.log('🔌 Socket already connected');
      this.isConnected = true;
      return this.socket;
    }

    // If socket exists but disconnected, try to reconnect
    if (this.socket && !this.socket.connected) {
      console.log('🔄 Socket exists but disconnected, attempting to reconnect...');
      const token = getToken();
      if (token) {
        // Update auth headers with new token
        this.socket.auth = { token };
        this.socket.io.opts.extraHeaders = { 'x-access-token': token };
        this.socket.connect();
        return this.socket;
      }
    }

    const token = getToken();
    if (!token) {
      console.error('❌ No auth token found. Cannot connect to socket.');
      return null;
    }

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:1111';

    console.log('🔌 Connecting to Socket.IO server...', API_BASE_URL);
    console.log('🔑 Using token:', token ? 'Token present' : 'No token');

    this.socket = io(API_BASE_URL, {
      // Authentication
      auth: {
        token: token,
      },
      extraHeaders: {
        'x-access-token': token,
      },
      
      // Transport configuration - try polling first for better compatibility with proxies
      transports: ['polling', 'websocket'],
      upgrade: true, // Allow upgrade from polling to websocket
      rememberUpgrade: true, // Remember successful upgrade
      
      // Reconnection settings
      reconnection: true,
      reconnectionAttempts: Infinity, // Keep trying to reconnect
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      
      // Timeout settings
      timeout: 20000,
      
      // Force new connection (don't reuse existing)
      forceNew: false,
      
      // Path (default is /socket.io/)
      path: '/socket.io/',
      
      // Additional options for production
      secure: API_BASE_URL.startsWith('https'),
      rejectUnauthorized: false, // Allow self-signed certificates in development
      
      // Polling options for better compatibility
      withCredentials: true,
      autoConnect: true,
    });

    // Connection event handlers
    this.socket.on('connect', () => {
      this.isConnected = true;
      console.log('✅ Socket connected successfully!');
      console.log('   Socket ID:', this.socket.id);
      console.log('   Transport:', this.socket.io.engine.transport.name);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('🔌 Socket disconnected:', reason);
      
      // Log specific disconnect reasons
      if (reason === 'io server disconnect') {
        console.warn('⚠️ Server disconnected the socket. Will attempt to reconnect...');
      } else if (reason === 'transport close') {
        console.warn('⚠️ Transport closed. Will attempt to reconnect...');
      } else if (reason === 'ping timeout') {
        console.warn('⚠️ Ping timeout. Will attempt to reconnect...');
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
      console.error('   Error type:', error.type);
      console.error('   Error description:', error.description);
      
      // Log specific error types
      if (error.message.includes('websocket')) {
        console.warn('⚠️ WebSocket connection failed, will try polling...');
      } else if (error.message.includes('Authentication')) {
        console.error('❌ Authentication failed. Token might be invalid or expired.');
      } else if (error.message.includes('CORS')) {
        console.error('❌ CORS error. Check server CORS configuration.');
      }
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Reconnection attempt #${attemptNumber}...`);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Reconnected after ${attemptNumber} attempts`);
      this.isConnected = true;
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after all attempts');
    });

    this.socket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });

    // Log transport upgrade
    this.socket.io.engine.on('upgrade', (transport) => {
      console.log('⬆️ Transport upgraded to:', transport.name);
    });

    return this.socket;
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  /**
   * Reconnect socket (useful after logout/login)
   */
  reconnect() {
    console.log('🔄 Manually reconnecting socket...');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
    return this.connect();
  }

  /**
   * Check and repair connection if needed
   */
  ensureConnected() {
    if (!this.socket || !this.socket.connected) {
      console.log('⚠️ Socket not connected, attempting to connect...');
      return this.connect();
    }
    return this.socket;
  }

  /**
   * Get socket instance
   * @returns {Socket|null} - Socket.IO instance or null
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Check if socket is connected
   * @returns {boolean} - Connection status
   */
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }

  /**
   * Emit an event to the server
   * @param {string} event - Event name
   * @param {*} data - Data to send
   */
  emit(event, data) {
    // Hand the event to Socket.IO even if we're briefly disconnected: it buffers
    // outgoing events and flushes them on reconnect. Gating on this.isConnected
    // (which can lag the real socket state) silently dropped messages instead.
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      console.warn('⚠️ Socket not initialized. Cannot emit event:', event);
    }
  }

  /**
   * Listen for an event from the server
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      
      // Store listener for cleanup
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
    }
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function (optional)
   */
  off(event, callback) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
        
        // Remove from listeners map
        if (this.listeners.has(event)) {
          const callbacks = this.listeners.get(event);
          const index = callbacks.indexOf(callback);
          if (index > -1) {
            callbacks.splice(index, 1);
          }
        }
      } else {
        // Remove all listeners for this event
        this.socket.off(event);
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Remove all listeners for all events
   */
  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.listeners.clear();
    }
  }

  // ============= CHAT SPECIFIC METHODS =============

  /**
   * Send a message
   * @param {Object} messageData - Message data
   * @param {number} messageData.receiver_id - Receiver user ID
   * @param {string} messageData.message - Message content
   * @param {number} [messageData.conversation_id] - Conversation ID (optional)
   * @param {string} [messageData.message_type] - Message type: 'text', 'image', 'file'
   * @param {string} [messageData.attachment_url] - Attachment URL (optional)
   */
  sendMessage(messageData) {
    this.emit('send_message', messageData);
  }

  /**
   * Start typing indicator
   * @param {number} conversationId - Conversation ID
   */
  startTyping(conversationId) {
    this.emit('typing_start', { conversation_id: conversationId });
  }

  /**
   * Stop typing indicator
   * @param {number} conversationId - Conversation ID
   */
  stopTyping(conversationId) {
    this.emit('typing_stop', { conversation_id: conversationId });
  }

  /**
   * Mark message as read
   * @param {number} messageId - Message ID
   * @param {number} conversationId - Conversation ID
   */
  markAsRead(messageId, conversationId) {
    this.emit('mark_read', { message_id: messageId, conversation_id: conversationId });
  }

  /**
   * Get online status of a user
   * @param {number} userId - User ID
   */
  getOnlineStatus(userId) {
    this.emit('get_online_status', { user_id: userId });
  }

  /**
   * Edit a message
   * @param {number} messageId - Message ID
   * @param {string} message - New message content
   * @param {number} conversationId - Conversation ID
   */
  editMessage(messageId, message, conversationId) {
    this.emit('edit_message', { message_id: messageId, message, conversation_id: conversationId });
  }

  /**
   * Delete a message
   * @param {number} messageId - Message ID
   * @param {number} conversationId - Conversation ID
   */
  deleteMessage(messageId, conversationId) {
    this.emit('delete_message', { message_id: messageId, conversation_id: conversationId });
  }

  /**
   * Listen for new messages
   * @param {Function} callback - Callback function
   */
  onNewMessage(callback) {
    this.on('new_message', callback);
  }

  /**
   * Listen for message sent confirmation
   * @param {Function} callback - Callback function
   */
  onMessageSent(callback) {
    this.on('message_sent', callback);
  }

  /**
   * Listen for message read notification
   * @param {Function} callback - Callback function
   */
  onMessageRead(callback) {
    this.on('message_read', callback);
  }

  /**
   * Listen for typing indicator
   * @param {Function} callback - Callback function
   */
  onUserTyping(callback) {
    this.on('user_typing', callback);
  }

  /**
   * Listen for user online status
   * @param {Function} callback - Callback function
   */
  onUserOnline(callback) {
    this.on('user_online', callback);
  }

  /**
   * Listen for user offline status
   * @param {Function} callback - Callback function
   */
  onUserOffline(callback) {
    this.on('user_offline', callback);
  }

  /**
   * Listen for user online status response
   * @param {Function} callback - Callback function
   */
  onUserOnlineStatus(callback) {
    this.on('user_online_status', callback);
  }

  /**
   * Listen for message edited
   * @param {Function} callback - Callback function
   */
  onMessageEdited(callback) {
    this.on('message_edited', callback);
  }

  /**
   * Listen for message deleted
   * @param {Function} callback - Callback function
   */
  onMessageDeleted(callback) {
    this.on('message_deleted', callback);
  }

  /**
   * Listen for errors
   * @param {Function} callback - Callback function
   */
  onError(callback) {
    this.on('error', callback);
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
